import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import { useSearchStore } from '../../store/searchStore';
import { useAssignmentRequestStore } from '../../store/assignmentRequestStore';
import { useCoachMoveFlagStore } from '../../store/coachMoveFlagStore';
import { useLeadStore } from '../../store/leadStore';
import { ChevronDown, Menu, X, Sun, Moon, Monitor, Settings, Search, HelpCircle, ClipboardList } from 'lucide-react';
import { devLoginEnabled } from '../../lib/devLogin';
import { buildNavModel, type NavGroup, type NavLinkItem } from './navModel';
import { NavMenu, NavMenuLink, NavMenuButton } from './NavMenu';

// CRM-35: Call Queues are hidden behind a build-time flag until the feature is
// ready for prime time. Off everywhere by default; mirrors enableDevLogin in App.tsx.
const enableCallQueues = import.meta.env.VITE_ENABLE_CALL_QUEUES === 'true';
// Email campaigns are hidden behind a build-time flag until the feature is
// ready for prime time. Off everywhere by default; mirrors enableCallQueues above.
const enableCampaigns = import.meta.env.VITE_ENABLE_EMAIL_CAMPAIGNS === 'true';

/** Count pill shown beside a nav label; hidden when there is nothing waiting. */
const Badge = ({ count }: { count?: number }) =>
  count !== undefined && count > 0 ? (
    <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full text-xs font-semibold bg-blast-600 text-white">
      {count}
    </span>
  ) : null;

/** Initials from the display name (first letter of the first two words), else the email. */
const getInitials = (displayName?: string | null, email?: string | null): string => {
  const words = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length > 0) return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const first = (email ?? '').trim()[0];
  return first ? first.toUpperCase() : '?';
};

const PROFILE_PATHS = ['/settings', '/admin', '/territories', '/dev/login'];

/**
 * Navigation - Top navigation bar
 * Work links, Manage / Tools dropdowns, and a profile menu (Settings, theme, admin, sign out).
 * Includes mobile hamburger menu and Blast brand accent bar
 */
export const Navigation = () => {
  const { user, logout, isAdmin, hasAnyRole } = useAuthStore();
  const { mode, cycleMode } = useThemeStore();
  const openSearch = useSearchStore((s) => s.open);
  const { pendingCount, fetchCount, myOpenCount, fetchMyOpenCount } = useAssignmentRequestStore();
  const { queueCount, callListCount, fetchCounts } = useCoachMoveFlagStore();
  const leadPendingCount = useLeadStore((s) => s.pendingCount);
  const fetchLeadCount = useLeadStore((s) => s.fetchCount);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Close any open dropdown on route change.
  useEffect(() => {
    setOpenMenu(null);
  }, [location.pathname]);

  useEffect(() => {
    if (hasAnyRole('MANAGER')) fetchCount();
    else if (user?.role === 'SALES_REP') fetchMyOpenCount();
  }, [user, hasAnyRole, fetchCount, fetchMyOpenCount]);

  useEffect(() => {
    if (hasAnyRole('DATA_STEWARD', 'MANAGER', 'INSIDE_SALES')) fetchCounts();
  }, [user, hasAnyRole, fetchCounts]);

  // Lead review is manager/admin work (ADMIN is a superset of MANAGER here).
  useEffect(() => {
    if (hasAnyRole('MANAGER')) fetchLeadCount();
  }, [user, hasAnyRole, fetchLeadCount]);

  const handleLogout = () => {
    logout();
    navigate('/sign-in');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Check if link is active
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Nav link styles
  const getClassesFor = (active: boolean) => {
    const base = 'px-3 py-2 text-sm font-medium rounded-md transition-colors';
    if (active) {
      return `${base} text-blast-600 dark:text-blast-400 bg-blast-50 dark:bg-blast-950`;
    }
    return `${base} text-gray-700 dark:text-gray-300 hover:text-blast-600 dark:hover:text-blast-400 hover:bg-gray-50 dark:hover:bg-gray-800`;
  };
  const getLinkClasses = (path: string) => getClassesFor(isActive(path));

  // A group's trigger is active when the current page belongs to one of its items.
  const groupActive = (g: NavGroup) => g.items.some((i) => isActive(i.to));
  const profileActive = PROFILE_PATHS.some(isActive);

  // Dropdown item styles (active item gets the same highlight as a top-level link)
  const getMenuItemClasses = (path: string) =>
    `flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium transition-colors ${
      isActive(path)
        ? 'text-blast-600 dark:text-blast-400 bg-blast-50 dark:bg-blast-950'
        : 'text-gray-700 dark:text-gray-300 hover:text-blast-600 dark:hover:text-blast-400 hover:bg-gray-50 dark:hover:bg-gray-800'
    }`;

  // Mobile nav link styles
  const getMobileLinkClasses = (path: string) => {
    const base = 'block px-3 py-2 text-base font-medium rounded-md transition-colors';
    if (isActive(path)) {
      return `${base} text-blast-600 dark:text-blast-400 bg-blast-50 dark:bg-blast-950`;
    }
    return `${base} text-gray-700 dark:text-gray-300 hover:text-blast-600 dark:hover:text-blast-400 hover:bg-gray-50 dark:hover:bg-gray-800`;
  };

  // Theme item icon and label (live from the theme store, so it updates while the menu stays open)
  const ThemeIcon = mode === 'light' ? Sun : mode === 'dark' ? Moon : Monitor;
  const themeLabel = mode === 'light' ? 'Light' : mode === 'dark' ? 'Dark' : 'System';

  const nav = buildNavModel({
    role: user?.role,
    hasAnyRole,
    isAdmin: isAdmin(),
    flags: { callQueues: enableCallQueues, campaigns: enableCampaigns, devLogin: devLoginEnabled },
    counts: { myOpen: myOpenCount, pending: pendingCount, leads: leadPendingCount, coachFlags: queueCount + callListCount },
  });

  // Label (plus icon where today's menu had one) and count for a nav item.
  const renderItemContent = (item: NavLinkItem, iconClass = 'w-4 h-4') => {
    const icon =
      item.key === 'requests' || item.key === 'my-requests' ? <ClipboardList className={iconClass} /> :
      item.key === 'settings' ? <Settings className={iconClass} /> :
      item.key === 'help' ? <HelpCircle className={iconClass} /> :
      null;
    if (!icon && item.count === undefined) return item.label;
    return (
      <span className="flex items-center gap-1">
        {icon} {item.label}
        <Badge count={item.count} />
      </span>
    );
  };

  return (
    <nav className="relative bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 border-t-4 border-t-[#b7ee41]">
      {/* Demo-environment corner ribbon — the same construction as Blast's `.header-corner`
          (red 50px border-triangle, 75px label rotated -45°, click-through) so the CRM reads as
          "not the live system" the way every other non-production Blast surface does. */}
      {devLoginEnabled && (
        <>
          <span className="sr-only">Demo environment — not the live CRM</span>
          <div aria-hidden="true" className="pointer-events-none absolute left-0 top-0 z-[9999] h-[100px] w-[100px] opacity-90">
            <span className="absolute left-0 top-0 h-0 w-0 border-[50px] border-[#c00] border-r-transparent border-b-transparent" />
            <p className="relative z-10 flex h-[75px] w-[75px] -rotate-45 items-center justify-center text-[1.375rem] font-bold tracking-[0.125rem] text-[#d0d0d0]">DEMO</p>
          </div>
        </>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left side - Logo and nav links */}
          <div className="flex items-center">
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 -ml-2 mr-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2" onClick={closeMobileMenu}>
              <img
                src="/blast-favicon-32x32.png"
                alt="Blast Athletics"
                className="w-8 h-8"
              />
              <span className="font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">Blast CRM</span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:ml-8 lg:flex lg:space-x-1">
              {nav.top.map((entry) => {
                if (entry.kind === 'link') {
                  return (
                    <Link key={entry.item.key} to={entry.item.to} className={getLinkClasses(entry.item.to)}>
                      {renderItemContent(entry.item)}
                    </Link>
                  );
                }
                const { group } = entry;
                return (
                  <NavMenu
                    key={group.key}
                    id={group.key}
                    openId={openMenu}
                    setOpenId={setOpenMenu}
                    triggerLabel={group.count > 0 ? `${group.label} menu, ${group.count} waiting` : `${group.label} menu`}
                    triggerClassName={`${getClassesFor(groupActive(group))} inline-flex items-center gap-1`}
                    trigger={
                      <>
                        {group.label}
                        <Badge count={group.count} />
                        <ChevronDown className="w-4 h-4" aria-hidden="true" />
                      </>
                    }
                  >
                    {group.items.map((item) => (
                      <NavMenuLink key={item.key} to={item.to} className={getMenuItemClasses(item.to)}>
                        {renderItemContent(item)}
                      </NavMenuLink>
                    ))}
                  </NavMenu>
                );
              })}
            </div>
          </div>

          {/* Right side - Search, help, and the profile menu */}
          <div className="flex items-center space-x-4">
            {/* Search button */}
            <button
              type="button"
              onClick={openSearch}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Search (⌘K)"
              aria-label="Open search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Help / Guide — opens /help/ in a new tab */}
            <a
              href="/help/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              title="Help & Guide"
              aria-label="Open help and guide"
            >
              <HelpCircle className="w-5 h-5" />
            </a>

            {user && (
              <NavMenu
                id="profile"
                openId={openMenu}
                setOpenId={setOpenMenu}
                align="right"
                triggerLabel={`Profile menu for ${user.displayName || user.email || 'user'}`}
                triggerClassName={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold bg-blast-100 text-blast-700 hover:bg-blast-200 transition-colors ${
                  profileActive ? 'ring-2 ring-blast-500' : ''
                }`}
                trigger={getInitials(user.displayName, user.email)}
              >
                <div aria-hidden="true" className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user.displayName || user.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{user.role}</p>
                </div>
                <NavMenuLink to="/settings" className={getMenuItemClasses('/settings')}>
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" /> Settings
                  </span>
                </NavMenuLink>
                <NavMenuButton keepOpen onClick={cycleMode}>
                  <span className="flex items-center gap-2">
                    <ThemeIcon className="w-4 h-4" /> Theme: {themeLabel}
                  </span>
                </NavMenuButton>
                {nav.profileAdmin.length > 0 && (
                  <>
                    <div className="my-1 border-t border-gray-100 dark:border-gray-700" role="separator" />
                    <div role="group" aria-label="Admin">
                      <p aria-hidden="true" className="px-3 pt-1 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">Admin</p>
                      {nav.profileAdmin.map((item) => (
                        <NavMenuLink key={item.key} to={item.to} className={getMenuItemClasses(item.to)}>
                          {item.label}
                        </NavMenuLink>
                      ))}
                    </div>
                  </>
                )}
                <div className="my-1 border-t border-gray-100 dark:border-gray-700" role="separator" />
                <NavMenuButton onClick={handleLogout}>Sign out</NavMenuButton>
              </NavMenu>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="px-4 py-3 space-y-1">
            {nav.mobileSections.map((section) => (
              <div key={section.heading}>
                <p className="px-3 pt-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  {section.heading}
                </p>
                {section.items.map((item) =>
                  item.external ? (
                    <a
                      key={item.key}
                      href={item.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={getMobileLinkClasses(item.to)}
                      onClick={closeMobileMenu}
                      aria-label="Open help and guide"
                    >
                      {renderItemContent(item)}
                    </a>
                  ) : (
                    <Link
                      key={item.key}
                      to={item.to}
                      className={getMobileLinkClasses(item.to)}
                      onClick={closeMobileMenu}
                    >
                      {renderItemContent(item)}
                    </Link>
                  ),
                )}
              </div>
            ))}
          </div>

          {/* Mobile user info */}
          {user && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {user.displayName || user.email}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{user.role}</p>
            </div>
          )}
        </div>
      )}
    </nav>
  );
};
